import React, {
	Dispatch,
	ReactElement,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	Page,
	PageSection,
	SidebarPanel,
	SidebarContent,
	Sidebar,
	List,
	Spinner,
	SelectOptionObject,
	Tooltip,
} from '@patternfly/react-core';
import Header from './header/Header';
import logo from '../img/zi-dark-logo.png';
import './Layout.scss';
import PageNavigationLinks, { INavigationLink } from './PageNavigationLinks';
import PageTabNavigationItems, { ITabNavigationItem } from './PageTabNavigationItems';
import { Outlet, useLocation } from 'react-router-dom';
import ApplicationProvider from '../components/user/ApplicationProvider';
import { headerMenuItems } from '../helpers/headerHelper';
import SidebarHeaders from './SidebarHeaders';
import { TDataframe } from '../api/types';
import { SideBarSearch } from '../types/sidebar';
import { BackendFactory } from 'dnd-core';
import { capitalizeFirstLetter, getAccessibleTextColor } from '../utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { useParams } from 'react-router-dom';
import WidgetLibrary from '../components/dnd/widgets/WidgetLibrary';
import _ from 'lodash';
import { useIsGranted } from '../components/user/ApplicationProvider';
import { CommonStoreProvider } from '../components/common-store/CommonStoreProvider';
import ToastProvider, {
	useToast,
} from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { ZoneSetting, ZoneSettingEnum } from '../api/zone/ZoneSetting';
import { isJsonObject } from '../components/util/Helpers';
import { AppThemeConfig } from '../helpers/sub-nav-contexts/sub-nav-context';
import { SessionTimeout } from '../components/modals/session/SessionTimeout';
import { Logout, Refresh } from '../api/login/Login';

export interface Folders {
	title: string;
	items: INavigationLink[];
}

export interface SideBar {
	navItems?: INavigationLink[];
	folders?: Folders;
	search?: SideBarSearch;
	isRight?: boolean;
}

export interface IConfig {
	subheaderContext?: ReactElement | null;
	sidebarContext?: INavigationLink[] | null;
	sidebarTabContext?: SideBarTabContext | null;
	sidebarHeaderContext?: SideBarHeaderContext | null;
	subNavExpanded?: boolean;
	setSubNavExpanded?: Dispatch<SetStateAction<boolean>>;
	folderHeadingTitle?: string | null; // Folders
	isLoading?: boolean;
	isRightSideBarLoading?: boolean;
	additionalClassNames?: string | null;
	sidebarSearch?: SideBarSearch;
	rightSideBar?: ReactElement;
	hideLeftSideBar?: boolean;
	folderType?: string;
	reloadFolders?: boolean;
	isFoldersLoading?: boolean;
	isLeftSidebarCollapsable?: boolean;
	startCollapsed?: boolean;
}

export type SubheaderSidebarContext = ((config: IConfig) => void)[];

export type OutletContext = {
	setSubSide: React.Dispatch<React.SetStateAction<IConfig>>;
	subNavExpanded: boolean;
	setSubNavExpanded: React.Dispatch<boolean>;
	folderHeadingTitle?: string | null;
	sidebarTabContext?: SideBarTabContext | null;
	sidebarHeaderContext?: SideBarHeaderContext | null;
	isLoading?: boolean;
	html5Backend?: BackendFactory;
	updateGridLayout?: (value: boolean) => void;
	updateGridBorder?: (value: boolean) => void;
};

export type OutletContextWithGridLayoutProps = {
	gridLayout?: boolean;
	gridBorder?: boolean;
	updateGridLayout?: (value: boolean) => void;
	updateGridBorder?: (value: boolean) => void;
};

export type GridLayoutOutletContext = OutletContext & OutletContextWithGridLayoutProps;
export type GridBorderOutletContext = OutletContext & OutletContextWithGridLayoutProps;

export type SideBarTabContext = {
	tabs: Array<{
		label: string;
		items: ITabNavigationItem[];
	}>;
	hasSearch: boolean;
	dataframes?: TDataframe[];
	onSelectDataframe?: (
		event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<Element>,
		value: string | SelectOptionObject,
		isPlaceholder?: boolean | undefined
	) => void;
	searchPlaceholder?: string;
};

export type SideBarHeaderContext = {
	header: string;
	subheader: string;
};

type LayoutProps = {
	isFullScreen?: boolean;
};

export default function Layout(props: LayoutProps): ReactElement {
	const { isFullScreen } = props;
	const [header, setHeader] = useState<React.ReactElement | null>(
		<Header
			logoImg={logo}
			menuItems={headerMenuItems}
		/>
	);
	const [subNavExpanded, setSubNavExpanded] = useState<boolean>(true);
	const [subSide, setSubSide] = useState<IConfig>({
		subheaderContext: null,
		sidebarContext: null,
		subNavExpanded: subNavExpanded,
		setSubNavExpanded: setSubNavExpanded,
		folderHeadingTitle: '',
		sidebarTabContext: null,
		sidebarHeaderContext: null,
		isLoading: false,
		isRightSideBarLoading: false,
		additionalClassNames: null,
		rightSideBar: undefined,
		hideLeftSideBar: false,
		folderType: undefined,
		reloadFolders: undefined,
		isFoldersLoading: undefined,
		isLeftSidebarCollapsable: false,
		startCollapsed: false,
	});
	const [subheading, setSubheading] = useState<ReactElement | null>(null);
	const [sidebar, setSidebar] = useState<ReactElement | null>(null);
	const [sidebarHeaders, setSidebarHeaders] = useState<ReactElement | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [isRightSideBarLoading, setIsRightSideBarLoading] = useState<boolean>(false);
	const [rightSideBar, setRightSideBar] = useState<ReactElement | null>(null);
	const [rightSideBarOpen, setRightSideBarOpen] = useState<boolean>(true);
	const isGranted = useIsGranted();
	const [leftSideBarOpen, setLeftSideBarOpen] = useState<boolean>(true);

	const pathname = useLocation().pathname;
	const isView = pathname.includes('/view/');
	const isPresentCreate = pathname.includes('/present/create');
	const { addToast } = useToast();
	const [isInactive, setIsInactive] = useState(false);

	const outletContext: OutletContext = {
		setSubSide,
		subNavExpanded,
		setSubNavExpanded,
	};

	const location = useLocation();
	const { dashboardId, presentationId, frameId, chartId } = useParams();

	const [showModal, setShowModal] = useState(false);
	const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
	const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

	// Only run on authenticated pages
	const isAuthPage = !location.pathname.includes('/login'); // adjust if needed

	// Schedule inactivity timeout
	const scheduleInactivityTimeout = useCallback(() => {
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}

		inactivityTimerRef.current = setTimeout(() => {
			setShowModal(true);
		}, INACTIVITY_LIMIT);
	}, []);

	// Track user activity
	const handleActivity = useCallback(() => {
		if (!isAuthPage) return;
		if (showModal) return; // don't reset while modal open

		scheduleInactivityTimeout();
	}, [showModal, isAuthPage, scheduleInactivityTimeout]);

	// Attach activity listeners
	useEffect(() => {
		if (!isAuthPage) return;

		const events = ['mousemove', 'keydown', 'click', 'scroll'];

		events.forEach((event) =>
			window.addEventListener(event, handleActivity, { passive: true })
		);

		// Start initial timer
		scheduleInactivityTimeout();

		return () => {
			events.forEach((event) => window.removeEventListener(event, handleActivity));

			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
			}
		};
	}, [handleActivity, isAuthPage, scheduleInactivityTimeout]);

	// Extend session
	const extendSession = () => {
		setShowModal(false);
		scheduleInactivityTimeout(); // restart 15-minute timer
		void Refresh(); // optional backend refresh
	};

	// Logout
	const logout = () => {
		setShowModal(false);

		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}

		void Logout().then(() => (window.location.href = '/login'));
	};

	useEffect(() => {
		if (subSide.isLeftSidebarCollapsable) {
			setLeftSideBarOpen(!subSide.startCollapsed);
		} else if (!leftSideBarOpen) {
			setLeftSideBarOpen(true);
		}
	}, [subSide.isLeftSidebarCollapsable, subSide.startCollapsed]);

	document.addEventListener('DOMContentLoaded', () => {
		document.querySelectorAll('.zi-table-row').forEach((busss) => {
			const button = busss.querySelector('button');
			if (button) {
				button.style.setProperty('color', 'initial', 'important');
				button.style.setProperty('background-color', 'initial', 'important');
			}
		});

		document.querySelectorAll('.remove-style').forEach((busss) => {
			const button = busss.querySelector('button');
			if (button) {
				// Reset styles to default
				button.style.setProperty('color', 'initial', 'important');
				button.style.setProperty('background-color', 'initial', 'important');
			}
		});
	});

	let currentApplicationBackgroundHeaderColor = localStorage.getItem(
		'AppBranding_BackgroundHeaderColor'
	);
	let currentApplicationBackgroundMenuColor = localStorage.getItem(
		'AppBranding_BackgroundMenuColor'
	);
	let currentApplicationBackgroundCanvasColor = localStorage.getItem(
		'AppBranding_BackgroundCanvasColor'
	);
	let currentApplicationButtonColor = localStorage.getItem('AppBranding_ButtonColor');
	let currentApplicationButtonHoverColor = localStorage.getItem('AppBranding_ButtonHoverColor');

	useEffect(() => {
		if (!currentApplicationBackgroundHeaderColor) {
			void ZoneSetting.Get(ZoneSettingEnum.applicationWindowTitle).then((response) => {
				if (response.enabled && isJsonObject(response.value)) {
					const decoded = JSON.parse(response.value) as AppThemeConfig;

					if (decoded.window_title) {
						pageTitle = decoded.window_title;
						localStorage.setItem('AppBranding_ApplicationTitle', pageTitle);
					}

					if (location.pathname) {
						const firstItem = capitalizeFirstLetter(location.pathname.split('/')[1]);
						pageTitle = `${firstItem} - ${pageTitle}`;
					}

					document.title = pageTitle;

					//set app header background color
					if (
						!currentApplicationBackgroundHeaderColor &&
						decoded.header_background_color
					) {
						localStorage.setItem(
							'AppBranding_BackgroundHeaderColor',
							decoded.header_background_color
						);
						localStorage.setItem(
							'AppBranding_BackgroundMenuColor',
							decoded.menu_background_color
						);
						localStorage.setItem(
							'AppBranding_BackgroundCanvasColor',
							decoded.canvas_background_color
						);
						localStorage.setItem('AppBranding_ButtonColor', decoded.button_color);
						localStorage.setItem(
							'AppBranding_ButtonHoverColor',
							decoded.button_hover_color
						);

						currentApplicationBackgroundHeaderColor =
							decoded.header_background_color != ''
								? decoded.header_background_color
								: null;
						currentApplicationBackgroundMenuColor =
							decoded.menu_background_color != ''
								? decoded.menu_background_color
								: null;
						currentApplicationBackgroundCanvasColor =
							decoded.canvas_background_color != ''
								? decoded.canvas_background_color
								: null;
						currentApplicationButtonColor =
							decoded.button_color != '' ? decoded.button_color : null;
						currentApplicationButtonHoverColor =
							decoded.button_hover_color != '' ? decoded.button_hover_color : null;
					}

					setHeader(
						<Header
							logoImg={logo}
							menuItems={headerMenuItems}
							backgroundHeaderColor={decoded.header_background_color}
							backgroundMenuColor={decoded.menu_background_color}
						/>
					);
				} else {
					if (pageTitle.startsWith(' -')) {
						pageTitle = pageTitle.replace(' -', '');
					}
					document.title = pageTitle;
				}
			});
		} else {
			setHeader(
				<Header
					logoImg={logo}
					menuItems={headerMenuItems}
					backgroundHeaderColor={currentApplicationBackgroundHeaderColor}
					backgroundMenuColor={currentApplicationBackgroundMenuColor ?? '#d2d2d2'}
				/>
			);
		}

		if (currentApplicationButtonColor == '') {
			currentApplicationButtonColor = null;
		}
		if (currentApplicationButtonHoverColor == '') {
			currentApplicationButtonHoverColor = null;
		}
		if (currentApplicationBackgroundHeaderColor == '') {
			currentApplicationBackgroundHeaderColor = null;
		}

		const styleId = 'dynamic-treeview-theme-style';
		const style = document.createElement('style');
		style.id = styleId;

		style.innerHTML = `
		.pf-c-tree-view__node.pf-m-current {
			background-color: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationBackgroundHeaderColor
			} !important;
			color: ${getAccessibleTextColor(currentApplicationBackgroundHeaderColor ?? '#002E57')} !important;
		}
	  
		.pf-c-tree-view__node:hover, .pf-c-tree-view__node:active {
			background-color: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationBackgroundHeaderColor
			} !important;
			color: ${getAccessibleTextColor(currentApplicationBackgroundHeaderColor ?? '#002E57')} !important;
		  }

		.pf-c-page__main-section {
			background: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationBackgroundHeaderColor
			} !important;
		}

		.pf-c-sidebar__content {
			background: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationBackgroundCanvasColor
			} !important;
		}

		.pf-c-dropdown__toggle:not(.no-color-override), .pf-c-button.pf-m-primary:not(.no-color-override) {
			background: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationButtonColor
			} !important;
			color: ${getAccessibleTextColor(currentApplicationButtonColor ?? '#0073c3')} !important;
		}

		.pf-c-dropdown__toggle:not(.no-color-override):hover,
		.pf-c-button.pf-m-primary:not(.no-color-override):hover {
		
			background: ${
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				currentApplicationButtonHoverColor
			} !important;
			color: ${getAccessibleTextColor(currentApplicationButtonHoverColor ?? '#0073c3')} !important;
		}
		`;
		if (currentApplicationBackgroundHeaderColor) {
			document.head.appendChild(style);
		}

		// 🟦 Update <p> elements with matching innerText
		['Home', 'Discover', 'Analyze', 'Report', 'Present'].forEach((text) => {
			const elements = Array.from(document.querySelectorAll('p')).filter(
				(el) => el.textContent?.trim() === text
			);

			elements.forEach((el) => {
				(el as HTMLElement).style.color = getAccessibleTextColor(
					currentApplicationBackgroundMenuColor ?? '#CCCCCC'
				);
			});
		});

		const currentApplicationTitle = localStorage.getItem('AppBranding_ApplicationTitle');
		//set app page title
		let pageTitle = 'ZeroedIn Workforce Analytics';

		if (location.pathname) {
			const firstItem = capitalizeFirstLetter(location.pathname.split('/')[1]);

			if (currentApplicationTitle?.startsWith(`${firstItem} -`)) {
				pageTitle = currentApplicationTitle;
			} else {
				//eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				pageTitle = `${firstItem} - ${currentApplicationTitle ?? pageTitle}`;
			}
		}
	}, []);

	useEffect(() => {
		if (subSide.subheaderContext) {
			setSubheading(
				<PageSection
					style={{ background: currentApplicationBackgroundHeaderColor ?? '#0073c3' }}
					component="div"
					className={`sub-nav ${!subNavExpanded ? 'collapsed' : ''} ${
						subSide.additionalClassNames ? subSide.additionalClassNames : ''
					}`}
				>
					{subSide.subheaderContext}
				</PageSection>
			);
		}
		if (subSide.sidebarContext) {
			setSidebar(
				<PageNavigationLinks
					sidebarLinks={subSide.sidebarContext}
					folderHeadingTitle={subSide.folderHeadingTitle}
					sidebarSearch={subSide.sidebarSearch}
					folderType={subSide.folderType}
					reloadFolders={subSide.reloadFolders}
					isFoldersLoading={subSide.isFoldersLoading}
				/>
			);
		} else if (subSide.hideLeftSideBar) {
			setSidebar(null);
		}

		if (subSide.sidebarTabContext) {
			setSidebar(<PageTabNavigationItems sidebarTabs={subSide.sidebarTabContext} />);
		}
		if (subSide.sidebarHeaderContext) {
			setSidebarHeaders(<SidebarHeaders sidebarHeaders={subSide.sidebarHeaderContext} />);
		}
		if (subSide.isLoading != undefined) {
			setLoading(subSide.isLoading);
		}

		if (subSide.isRightSideBarLoading != undefined) {
			setIsRightSideBarLoading(subSide.isRightSideBarLoading);
		}

		const isNotViewAndIsEntityId =
			!isView && (presentationId || isPresentCreate || dashboardId);
		const isChartId = chartId !== undefined;

		if (subSide.rightSideBar) {
			setRightSideBar(subSide.rightSideBar);
		} else {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const ls_rightSideBarData = JSON.parse(
				localStorage.getItem('rightSideBarData') ?? '{}'
			);

			if (
				!_.isEmpty(ls_rightSideBarData) &&
				(dashboardId || (presentationId && !isView)) &&
				!frameId
			) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const rightSideBarData: React.ReactElement<
					any,
					string | React.JSXElementConstructor<any>
				> = ls_rightSideBarData;

				setRightSideBar(<WidgetLibrary {...rightSideBarData} />);
			} else {
				setRightSideBar(null);
			}
		}
	}, [subSide, setSubSide]);

	return (
		<ToastProvider>
			<ApplicationProvider>
				<CommonStoreProvider>
					{isFullScreen ? (
						<Outlet context={outletContext} />
					) : (
						<>
							<Page header={header}>
								{subheading}
								<PageSection
									className="page-content"
									style={{
										minHeight: '500px',
										padding: '0',
										marginRight: '0',
									}}
								>
									<Sidebar className="zi-sidebar">
										{sidebar && (
											<SidebarPanel
												className={`left-sidebar-panel ${
													leftSideBarOpen ? 'open' : 'closed-left'
												}`}
											>
												{loading ? (
													<div className="loading-container">
														<Spinner size={'xl'} />
													</div>
												) : (
													<React.Fragment>
														{sidebarHeaders}
														{subSide.isLeftSidebarCollapsable && (
															<Tooltip content={'Toggle Menu'}>
																<FontAwesomeIcon
																	icon={
																		leftSideBarOpen
																			? faAngleLeft
																			: faAngleRight
																	}
																	size="2xl"
																	className="collapse-icon-left"
																	onClick={() =>
																		setLeftSideBarOpen(
																			!leftSideBarOpen
																		)
																	}
																/>
															</Tooltip>
														)}

														<List
															className="zi-sidebar-links"
															isPlain
														>
															{sidebar ? <li>{sidebar}</li> : null}
														</List>
													</React.Fragment>
												)}
											</SidebarPanel>
										)}
										<SidebarContent
											className={`page-main-content ${
												sidebar ? '' : 'no-side-bar'
											}`}
										>
											<Outlet context={outletContext} />
										</SidebarContent>
										{rightSideBar && (
											<SidebarPanel
												className={`right-sidebar-panel ${
													rightSideBarOpen ? 'open' : 'closed'
												}`}
											>
												{isRightSideBarLoading ? (
													<div className="loading-container">
														<Spinner size={'xl'} />
													</div>
												) : (
													<>
														<Tooltip content={'Toggle Menu'}>
															<FontAwesomeIcon
																icon={
																	rightSideBarOpen
																		? faAngleRight
																		: faAngleLeft
																}
																size="2xl"
																className="collapse-icon"
																onClick={() =>
																	setRightSideBarOpen(
																		!rightSideBarOpen
																	)
																}
															/>
														</Tooltip>
														<List
															className="zi-sidebar-links"
															isPlain
														>
															{rightSideBar}
														</List>
													</>
												)}
											</SidebarPanel>
										)}
									</Sidebar>
									<div
										className="ribbon-footer"
										style={{
											background:
												currentApplicationBackgroundHeaderColor ??
												'#0073c3',
											color: getAccessibleTextColor(
												currentApplicationBackgroundHeaderColor ?? '#002E57'
											),
										}}
									>
										<span className="ribbon-label">Powered by ZeroedIn ©</span>
									</div>
								</PageSection>
								<SessionTimeout
									showModal={showModal}
									setShowModal={setShowModal}
									extendSession={extendSession}
									logout={logout}
								/>
							</Page>
						</>
					)}
				</CommonStoreProvider>
			</ApplicationProvider>
		</ToastProvider>
	);
}
